import React from 'react';
// eslint-disable-next-line import/no-webpack-loader-syntax
import mapboxgl from '!mapbox-gl'; // https://docs.mapbox.com/mapbox-gl-js/guides/install/#excluding-mapbox-gl-js-explicitly-from-transpilation
import { Resizable } from "re-resizable";
import PropTypes from 'prop-types';
import RavenUtil from 'APP_ROOT/components/common/ravenUtil';
import MapboxHelper from 'APP_ROOT/components/mapbox/mapboxHelperDashboard';
import MapboxGeocoder from '@mapbox/mapbox-gl-geocoder';
import { getCurrentLocale } from 'APP_ROOT/localization';
import ProgressOverlay from 'APP_ROOT/components/common/ProgressOverlay';

const KeyCode = {
    ENTER: 13,
    ESCAPE: 27,
};

const MIN_DIAMETER_PX = 96;
const DEFAULT_DIAMETER_PX = MIN_DIAMETER_PX * 3.33333; // 3.33333 is a ratio which will default to a 100 m wide editor circle, but allow it to be shrunk to minimum 30 m at the same zoom level: redux const DEFAULT_RADIUS divided by MIN_RADIUS_KM (100 meters / 30 meters)

const MIN_RADIUS_KM = 0.03;
const MAX_RADIUS_KM = 100;
//SEE ALSO const DEFAULT_RADIUS in meters in src/data/geofences/constants.js (on-mount zoom-to-fit default, also geocoder result onGeofenceAddressChange default)

const coordinatesEqual = (coords1, coords2) => {
    return coords1 && coords2 && coords1[0] === coords2[0] && coords1[1] === coords2[1];
}

// Creating these object props once should let Resizable avoid some
// unnecessary renders (it's a PureComponent)
const RESIZABLE_PROPS = {
    style: {
        background: 'transparent'
    },
    enable: {
        right: true
    },
    handleComponent: {
        right: <div className="handle-right" />
    }
};

export default class GeofenceEditor extends React.PureComponent {

    static propTypes = {
        mapboxMap: PropTypes.instanceOf(mapboxgl.Map), // for center location and zoom level, loaded async so assuming it shouldn't be required on initial render
        detailsPanelIsOpen: PropTypes.bool.isRequired,
        color: PropTypes.string,
        colorStatus: PropTypes.string,
        location: PropTypes.instanceOf(Array),
        radiusInKm: PropTypes.number,
        saving: PropTypes.bool,
        onGeofenceAddressChange: PropTypes.func.isRequired,
        onGeofenceLocationChange: PropTypes.func.isRequired,
        onGeofenceRadiusChange: PropTypes.func.isRequired,
        saveChanges: PropTypes.func.isRequired,
        cancelChanges: PropTypes.func.isRequired,
    }

    state = {
        editModeInitiated: false,
        size: {
            width: DEFAULT_DIAMETER_PX,
            height: DEFAULT_DIAMETER_PX
        }
    }

    // properties shared between GeofenceEditor and map state change event handlers:
    geofenceDiameterInPixels = DEFAULT_DIAMETER_PX;
    geocoderSearchControl;

    onGeocoderResult = (data) => {
        this.props.onGeofenceAddressChange(data.result.place_name);
    }

    onMapChange = (event) => {

        if (!this.state.editModeInitiated) return;

        this.updateGeofenceRadius(this.geofenceDiameterInPixels); // recalculate current circle width on new map dimensions
        this.updateGeofenceLocation();
    }

    onKeyDown = event => {
        // TODO: support ENTER key save action when we sort out how to make it
        // play nice with EditableLabel.
        switch (event.keyCode) {
            case KeyCode.ESCAPE:
                this.props.cancelChanges();
                break;
            default:
                break;
        }
    }

    componentDidMount = () => {

        const { mapboxMap } = this.props;
        mapboxMap.on("moveend", this.onMapChange);
        mapboxMap.on("zoomend", this.onMapChange); // tapping zoom in/oput buttons can alter center location as well

        // adding a control to top/top-center is not supported! (https://github.com/mapbox/mapbox-gl-js/issues/3039)
        this.geocoderSearchControl = new MapboxGeocoder({
            accessToken: process.env.REACT_APP_MAPBOX_ACCESS_TOKEN,
            mapboxgl: mapboxgl,
            language: getCurrentLocale(),
            marker: false //https://github.com/mapbox/mapbox-gl-geocoder/blob/master/API.md#parameters
        });
        this.geocoderSearchControl.on("result", this.onGeocoderResult);
        mapboxMap.addControl(
            this.geocoderSearchControl,
            'top-left' // hijacked and set to top/top-center behaviour in _geofenceEditor.scss
        );

        if (this.props.location) {
            this.zoomToFitGeofence();
        } else {
            const coordinates = mapboxMap.getCenter().toArray();
            this.props.onGeofenceLocationChange(coordinates);
        }

		document.addEventListener("keydown", this.onKeyDown, false);
    }

    componentDidUpdate = (prevProps, prevState) => {
        // transitioning from uninitialised new geofence, to initialised
        if (!prevProps.location && this.props.location) {
            this.zoomToFitGeofence();
        }

        if (!prevState.editModeInitiated && this.state.editModeInitiated) {
            this.props.setGeofenceLayersVisible(false);
        }
    }

    componentWillUnmount = () => {
        this.props.mapboxMap.off("moveend", this.onMapChange);
        this.props.mapboxMap.off("zoomend", this.onMapChange);
        this.props.mapboxMap.off("moveend", this.onInitialMoveEnd);
        this.geocoderSearchControl.off("result", this.onGeocoderResult);
        this.props.mapboxMap.removeControl(this.geocoderSearchControl);
        this.props.setGeofenceLayersVisible(true);
        this.restoreInitialMapZoomExtents();
		document.removeEventListener("keydown", this.onKeyDown, false);
    }

    updateGeofenceRadius = (width) => {
        const radiusInKm = this.getCircleRadiusInKm(width);
        if (radiusInKm && this.props.radiusInKm !== radiusInKm) {
            this.props.onGeofenceRadiusChange(radiusInKm);
        }
    }

    updateGeofenceLocation = () => { // location is always the center of the map

        if (!this.props.mapboxMap) return;

        const center = this.props.mapboxMap.getCenter();
        /* Alternative for center location is to use the current bounds.
           Although used when converting pixels to radiusInKm, bounds seems
           less accurate when updating location
        const bounds = this.props.mapboxMap.getBounds();
        bounds.getCenter();
        */
        const coordinates = center.toArray().map(val => RavenUtil.round(val, 5));
        if (!coordinatesEqual(this.props.location, coordinates)) {
            this.props.onGeofenceLocationChange(coordinates);
        }
    }

    onResize = (event, direction, refToElement, delta) => { // https://github.com/bokuweb/re-resizable#basic

        if (!refToElement.offsetWidth) { // https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetWidth
            console.error("Geofence Width unknown onSizeChange");
            return;
        }

        this.updateGeofenceRadius(refToElement.offsetWidth);

        this.geofenceDiameterInPixels = refToElement.offsetWidth;
    }

    onResizeStop = (event, direction, refToElement, delta) => {
        this.setState(state => {
            const diameter = this.state.size.width + delta.width;
            return {
                size: {
                    width: diameter,
                    height: diameter
                }
            };
        });
    }

    onInitialMoveEnd = e => {
        if (e && e.startedBy === 'zoomToFitGeofence') {
            this.props.mapboxMap.off('moveend', this.onInitialMoveEnd);
            if(e.overrideDefaultSize) {
                const calculatedRadiusInKm = this.getCircleRadiusInKm(this.geofenceDiameterInPixels);
                if (calculatedRadiusInKm !== this.props.radiusInKm) {
                    this.geofenceDiameterInPixels = Math.round(this.props.radiusInKm / calculatedRadiusInKm * this.geofenceDiameterInPixels);
                    this.setState({
                        size: {
                            width: this.geofenceDiameterInPixels,
                            height: this.geofenceDiameterInPixels
                        }
                    });
                }
            }
            this.setState({
                editModeInitiated: true
            });
        }
    }

    zoomToFitGeofence = () => {

        const { mapboxMap, location, radiusInKm } = this.props;
        const cameraOptions = this.cameraForGeofence(mapboxMap, location, radiusInKm, this.geofenceDiameterInPixels);
        cameraOptions.pitch = 0;
        mapboxMap.on("moveend", this.onInitialMoveEnd);
        this.setMapZoomExtents();
        mapboxMap.easeTo(cameraOptions, {
            startedBy: 'zoomToFitGeofence',
            overrideDefaultSize: cameraOptions.zoom > mapboxMap.getMaxZoom()
        });
    }

    cameraForGeofence = (mapboxMap, location, radiusInKm, diameterInPx) => {
        const bounds = MapboxHelper.getBoundsFromRadiusInKm(location, radiusInKm);
        const mapContainer = mapboxMap.getCanvasContainer();

        let verticalPadding = diameterInPx ? (mapContainer.clientHeight - diameterInPx) / 2 : 0;
        if (verticalPadding < 1) verticalPadding = 0;

        let horizontalPadding = diameterInPx ? (mapContainer.clientWidth - diameterInPx) / 2 : 0;
        if (horizontalPadding < 1) horizontalPadding = 0;
        return mapboxMap.cameraForBounds(bounds, {
            padding: {
                top: verticalPadding,
                right: horizontalPadding,
                bottom: verticalPadding,
                left: horizontalPadding
            }
        });
    }

    setMapZoomExtents = () => {
        const { location, mapboxMap } = this.props;
        if (!location || !location[0]) {
            console.error('GeofenceEditor: setZoomExtentsOnMap requires a valid location from props');
            return;
        }

        this._initialMapZoomExtents = [
            mapboxMap.getMinZoom(),
            mapboxMap.getMaxZoom()
        ];

        const minZoom = this.cameraForGeofence(mapboxMap, location, MAX_RADIUS_KM).zoom;
        const maxZoom = this.cameraForGeofence(mapboxMap, location, MIN_RADIUS_KM, MIN_DIAMETER_PX).zoom;
        mapboxMap.setMinZoom(minZoom);
        mapboxMap.setMaxZoom(maxZoom);
    }

    restoreInitialMapZoomExtents = () => {
        if (this._initialMapZoomExtents) {
            const { mapboxMap } = this.props;
            const [ minZoom, maxZoom ] = this._initialMapZoomExtents;
            mapboxMap.setMinZoom(minZoom);
            mapboxMap.setMaxZoom(maxZoom);
            this._initialMapZoomExtents = null;
        }
    }

    getCircleRadiusInKm = (width) => {

        const bounds = this.props.mapboxMap.getBounds();
    
        const mapContainer = this.props.mapboxMap.getCanvasContainer();

        const clientWidth = mapContainer.clientWidth;

        const mapWidthInDegrees = bounds.getWest() - bounds.getEast();

        const ratio = width / clientWidth;

        if (ratio > 1.0) {
            console.warn("Geofence width is greater than map.  Ratio is invalid.");
            return undefined;
        }

        var center = bounds.getCenter();  // map center and bounds center can be different, using bounds center
        /*
        var currentMapCenter = this.props.mapboxMap.getCenter();
        console.log("Map center versus bounds center is " +
                    "\n    " + JSON.stringify(currentMapCenter) +
                    "\n    " + JSON.stringify(center));
        */

        const lng1 = center.lng - mapWidthInDegrees/2 * ratio;
        const lng2 = center.lng + mapWidthInDegrees/2 * ratio;
    
        const diameterInKm = RavenUtil.distance(center.lat, lng1, center.lat, lng2, "K");
        const radiusInKm = diameterInKm/2;

        // round to the nearest 0.001 (or metre)
        return RavenUtil.round(radiusInKm, 3);
    }

    render () {

        const { saving } = this.props;

        return (
            <div className={this.state.editModeInitiated ? "geofence-editor edit-mode" : "geofence-editor"}>
                <div className="geofence-canvas">
                    <Resizable
                        {...RESIZABLE_PROPS}
                        size={this.state.size}
                        minWidth={MIN_DIAMETER_PX}
                        maxWidth="100%" // stop right slider at left-most edge (prevents distorting the circle, and is required for ratio in getCircleRadiusInKm())
                        resizeRatio={2} // required since the resizable is centered
                        lockAspectRatio={true}
                        onResize={this.onResize}
                        onResizeStop={this.onResizeStop}
                    >
                        <div className="circle-aspect-ratio-lock">
                            <div className="circle-background" style={{
                                backgroundColor: this.props.color
                            }}></div>
                            <div className="circle-foreground"></div>
                        </div>
                    </Resizable>
                </div>
                { saving && <ProgressOverlay/> }
            </div>
        )
    }
}
